import { useState } from "react";
import { X, Gift, Save } from "lucide-react";
import { User } from "../../../../types/features/administration/User";
import BaseModal from "../../../ui/Modal";
import usePromoCodes from "../../../../hooks/administration/users/usePromoCodes";

// UpdateRewardModal.tsx
interface UpdateRewardModalProps {
  user: User;
  currentParrainAmount: number; // Montant actuel pour parrain
  currentFilleuleAmount: number; // Montant actuel pour filleule
  onClose: () => void;
  onUpdate: (userId: string, amountParrain: number, amountFilleule: number) => Promise<void>;
}

export default function UpdateRewardModal({
  user,
  currentParrainAmount,
  currentFilleuleAmount,
  onClose,
  onUpdate,
}: UpdateRewardModalProps) {
  const [parrainAmount, setParrainAmount] = useState<string>(currentParrainAmount?.toString() || "0");
  const [filleuleAmount, setFilleuleAmount] = useState<string>(currentFilleuleAmount?.toString() || "0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { promoCode } = usePromoCodes({ userId: user.id });

  const currency =  "CAD";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const numericParrain = parseFloat(parrainAmount);
    const numericFilleule = parseFloat(filleuleAmount);

    if (isNaN(numericParrain) || numericParrain < 0) {
      setError("Veuillez entrer un montant valide pour le parrain");
      return;
    }

    if (isNaN(numericFilleule) || numericFilleule < 0) {
      setError("Veuillez entrer un montant valide pour la filleule");
      return;
    }

    setLoading(true);
    try {
      await onUpdate(user.id, numericParrain, numericFilleule);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal onClose={onClose} isOpen={!!user} title="Modifier l'utilisateur" size="auto" >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-green-600" />
            <h2 className="text-xl font-semibold text-gray-900">Modifier les montants de parrainage</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Informations utilisateur */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <span className="text-gray-500">Utilisateur:</span>
              <span className="font-medium text-gray-900">{user.email || user.name}</span>

              <span className="text-gray-500">Code promo:</span>
              <span className="font-medium text-gray-900">{promoCode?.user_promo_code || "Non défini"}</span>

              <span className="text-gray-500">Devise:</span>
              <span className="font-medium text-gray-900">{currency}</span>
            </div>
          </div>

          {/* Montants actuels */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <p className="text-sm font-medium text-blue-800">Montants actuels :</p>
            <p className="text-sm text-blue-800">
              Parrain :{" "}
              <span className="font-bold">
                {currentParrainAmount || 0} {currency}
              </span>
            </p>
            <p className="text-sm text-blue-800">
              Filleule :{" "}
              <span className="font-bold">
                {currentFilleuleAmount || 0} {currency}
              </span>
            </p>
          </div>

          {/* Nouveau montant - Parrain */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau montant pour le parrain ({currency})
            </label>
            <input
              type="number"
              value={parrainAmount}
              onChange={(e) => setParrainAmount(e.target.value)}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder={`Ex: 100 ${currency}`}
              required
              autoFocus
            />
          </div>

          {/* Nouveau montant - Filleule */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nouveau montant pour la filleule ({currency})
            </label>
            <input
              type="number"
              value={filleuleAmount}
              onChange={(e) => setFilleuleAmount(e.target.value)}
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder={`Ex: 50 ${currency}`}
              required
            />
            <p className="mt-1 text-xs text-gray-500">Montant crédité à la filleule lors d'un parrainage réussi</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {loading ? (
                "Mise à jour..."
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </BaseModal>
  );
}
